<script setup lang="ts">
import { Mask } from 'maska';

type Props = {
  typedCpf?: string;
  hasError?: boolean;
};

const { typedCpf, hasError } = defineProps<Props>();

type Emits = {
  (e: 'on-focus'): void;
};

const emit = defineEmits<Emits>();

const registerData = defineModel<any>('modelValue', {
  required: false,
});

const { t } = useTexts();
const {
  userId,
  userAddressZipcode,
  userCountry,
  userAddressFullAddress,
  userAddressCity,
  userAddressState,
} = useUserData();

const { isRequestAddressOnRegister } = useAuth();

const zipCodeFoundByApi = ref(false);
const zipStreetFoundByApi = ref(false);
const zipCityFoundByApi = ref(false);
const zipStateFoundByApi = ref(false);
const isZipLoading = ref(false);
const isMounted = ref(false);

const maskByCountryZipCode = computed<string[] | string>(() => {
  return userCountry.value === 'BRA' ? '#####-###' : '';
});

const maskedZipCode = computed<string>(() => {
  const mask = new Mask({ mask: maskByCountryZipCode.value });

  return mask.masked(registerData.value.zipcode);
});

const unmaskedZipCode = computed<string>(() => {
  const mask = new Mask({ mask: maskByCountryZipCode.value });

  return mask.unmasked(registerData.value.zipcode);
});

function checkZipCodeField() {
  if (userCountry.value !== 'BRA') {
    return;
  }

  if (unmaskedZipCode.value.length === 8) {
    registerData.value.zipcode = unmaskedZipCode.value;
    getZipInfos();
  }
}

const isZipValid = ref(false);

async function getZipInfos({ byCpf = false }: { byCpf?: boolean } = {}) {
  if (
    !isMounted.value ||
    (byCpf && !typedCpf) ||
    (!byCpf && !unmaskedZipCode.value) ||
    zipCodeFoundByApi.value
  ) {
    return;
  }

  const endpoint = byCpf ? '/bff/address-by-cpf' : 'apicep';

  const payload = byCpf
    ? { cpf: typedCpf }
    : { cep: unmaskedZipCode.value, user_id: userId.value || '' };

  const setAddressEdit = ref(false);
  const setFocusOnStreet = ref(false);

  isZipLoading.value = true;

  try {
    const { ApiService } = useApiService();

    const { status, data } = await ApiService.post(endpoint, payload);

    if (status === 200) {
      isZipValid.value = true;

      if (data.zipcode || data.cep) {
        const zipcode = data.zipcode || data.cep || '';

        registerData.value.zipcode = zipcode;
        zipCodeFoundByApi.value = true;

        if (data.street || data.logradouro) {
          const street = data.street || data.logradouro || '';

          registerData.value.address = street;
          zipStreetFoundByApi.value = true;
        } else {
          setAddressEdit.value = true;
          setFocusOnStreet.value = true;
        }

        if (data.city || data.cidade) {
          const city = data.city || data.cidade || '';

          registerData.value.city = city;
          zipCityFoundByApi.value = true;
        } else {
          setAddressEdit.value = true;
        }

        if (data.uf || data.state) {
          const state = data.uf || data.state || '';

          registerData.value.state = state;
          zipStateFoundByApi.value = true;
        } else {
          setAddressEdit.value = true;
        }
      } else {
        setAddressEdit.value = true;
      }

      if (setAddressEdit.value) {
        isEditing.value = true;
      }

      if (setFocusOnStreet.value) {
        nextTick(() => {
          document.getElementById('address')?.focus();
        });
      }
    } else {
      registerData.value.address = '';
      registerData.value.city = '';
      registerData.value.state = '0';
      zipCodeFoundByApi.value = false;
      zipStreetFoundByApi.value = false;
      zipCityFoundByApi.value = false;
      zipStateFoundByApi.value = false;
      isZipValid.value = false;
      isEditing.value = !byCpf;
    }
  } catch (error) {
    zipCodeFoundByApi.value = false;
    zipStreetFoundByApi.value = false;
    zipCityFoundByApi.value = false;
    zipStateFoundByApi.value = false;
    isZipValid.value = false;
    isEditing.value = !byCpf;
  } finally {
    isZipLoading.value = false;
  }
}

const allBrazilianStates = [
  { value: '0', label: t('general.select') },
  { value: 'AC', label: 'Acre (AC)' },
  { value: 'AL', label: 'Alagoas (AL)' },
  { value: 'AP', label: 'Amapá (AP)' },
  { value: 'AM', label: 'Amazonas (AM)' },
  { value: 'BA', label: 'Bahia (BA)' },
  { value: 'CE', label: 'Ceará (CE)' },
  { value: 'DF', label: 'Distrito Federal (DF)' },
  { value: 'ES', label: 'Espírito Santo (ES)' },
  { value: 'GO', label: 'Goiás (GO)' },
  { value: 'MA', label: 'Maranhão (MA)' },
  { value: 'MT', label: 'Mato Grosso (MT)' },
  { value: 'MS', label: 'Mato Grosso do Sul (MS)' },
  { value: 'MG', label: 'Minas Gerais (MG)' },
  { value: 'PA', label: 'Pará (PA)' },
  { value: 'PB', label: 'Paraíba (PB)' },
  { value: 'PR', label: 'Paraná (PR)' },
  { value: 'PE', label: 'Pernambuco (PE)' },
  { value: 'PI', label: 'Piauí (PI)' },
  { value: 'RJ', label: 'Rio de Janeiro (RJ)' },
  { value: 'RN', label: 'Rio Grande do Norte (RN)' },
  { value: 'RS', label: 'Rio Grande do Sul (RS)' },
  { value: 'RO', label: 'Rondônia (RO)' },
  { value: 'RR', label: 'Roraima (RR)' },
  { value: 'SC', label: 'Santa Catarina (SC)' },
  { value: 'SP', label: 'São Paulo (SP)' },
  { value: 'SE', label: 'Sergipe (SE)' },
  { value: 'TO', label: 'Tocantins (TO)' },
];

const isEditing = ref(false);

function handleToggleEditing() {
  isEditing.value = !isEditing.value;

  if (!registerData.value.address && isEditing.value) {
    nextTick(() => {
      document.getElementById('address')?.focus();
    });
  }
}

function handleRemoveAddress() {
  isZipValid.value = false;
  registerData.value.address = '';
  registerData.value.city = '';
  registerData.value.state = '0';
  registerData.value.zipcode = '';
  zipCodeFoundByApi.value = false;
  zipStreetFoundByApi.value = false;
  zipCityFoundByApi.value = false;
  zipStateFoundByApi.value = false;
}

const anyChanges = computed(() => {
  const registerFields = registerData.value;

  return (
    registerFields.zipcode !== userAddressZipcode.value ||
    registerFields.address !== userAddressFullAddress.value ||
    registerFields.state !== userAddressState.value ||
    registerFields.city !== userAddressCity.value ||
    registerFields.country_data !== userCountry.value
  );
});

const hasFullAddress = computed(() => {
  return (
    !!registerData.value.address &&
    !!registerData.value.city &&
    !!registerData.value.state
  );
});

defineExpose({
  hasError,
  anyChanges,
  setEditing: handleToggleEditing,
  removeAddress: handleRemoveAddress,
});

onMounted(() => {
  nextTick(() => {
    isMounted.value = true;
  });
});

watch(
  () => typedCpf,
  (newValue, oldValue) => {
    if (!isRequestAddressOnRegister.value) {
      return;
    }

    if (newValue && oldValue !== newValue) {
      getZipInfos({ byCpf: true });
    }
  },
);

watch(() => unmaskedZipCode.value, checkZipCodeField);
</script>

<template>
  <div :class="$style.addressContainer">
    <div :class="$style.zipcodeField">
      <div
        v-if="hasFullAddress"
        :class="$style.addressDetails"
      >
        <div :class="$style.addressValues">
          <div
            v-if="registerData.address"
            :class="$style.addressStreet"
          >
            {{ registerData.address }}
          </div>
          <div :class="$style.addressRow">
            <b v-if="registerData.zipcode">
              {{ maskedZipCode }}
            </b>
            <span :class="$style.pipe">|</span>
            <span v-if="registerData.city">
              {{ registerData.city }}
            </span>
            <span :class="$style.pipe">|</span>
            <span v-if="registerData.state">
              {{ registerData.state }}
            </span>
          </div>
        </div>
      </div>

      <BaseFormInput
        v-else
        id="zipcode"
        v-model="registerData.zipcode"
        :data-maska="maskByCountryZipCode"
        :disabled="isZipLoading"
        :has-error="hasError"
        :initial-value="registerData.zipcode"
        :label="$t('account.user_data.zipcode')"
        name="zipcode"
        placeholder="ex: 98765-123"
        type="tel"
        @focus="emit('on-focus')"
      />

      <span
        v-if="isZipLoading"
        class="absolute bottom-0 right-0 mb-4 mr-6"
      >
        <NuxtIcon
          class="animate-spin w-4 h-4 mx-auto text-texts/40"
          name="loading"
        />
      </span>

      <div
        v-else
        :class="$style.buttons"
      >
        <button
          v-if="!isZipValid && !hasFullAddress"
          :class="$style.validateButton"
          :disabled="registerData.zipcode.length !== 9 || isZipLoading"
          @click="getZipInfos()"
        >
          {{ $t('general.validate') }}
        </button>
        <button
          v-else
          :class="$style.changeButton"
          @click="handleToggleEditing"
        >
          {{ $t('general.edit') }}
        </button>
        <button
          v-if="hasFullAddress"
          :class="$style.removeButton"
          @click="handleRemoveAddress"
        >
          <NuxtIcon
            class="text-xs"
            name="close"
          />
        </button>
      </div>
    </div>

    <div
      v-show="isEditing"
      :class="$style.editFullAdress"
    >
      <BaseFormInput
        id="address"
        v-model="registerData.address"
        class="w-full"
        :initial-value="registerData.address"
        :label="$t('account.user_data.full_address')"
        name="address"
        placeholder="ex: Rua das Flores, 567, Ap 1202"
        type="text"
        @focus="emit('on-focus')"
      />

      <div class="flex gap-2">
        <BaseFormInput
          id="city"
          v-model="registerData.city"
          :disabled="zipCityFoundByApi"
          :initial-value="registerData.city"
          :label="$t('account.user_data.city')"
          name="city"
          placeholder="ex: Belo Horizonte"
          type="text"
          @focus="emit('on-focus')"
        />

        <BaseFormSelectField
          v-if="userCountry === 'BRA'"
          id="state"
          v-model="registerData.state"
          :disabled="zipStateFoundByApi"
          :initial-value="registerData.state"
          :label="$t('account.user_data.state')"
          name="state"
          :options="allBrazilianStates"
          placeholder="ex: MG"
          @focus="emit('on-focus')"
        />
        <BaseFormInput
          v-else
          id="state"
          v-model="registerData.state"
          name="state"
          placeholder="ex: MG"
          type="text"
        />

        <button
          :class="$style.editButton"
          @click="handleToggleEditing"
        >
          <NuxtIcon name="check" />
        </button>
      </div>
    </div>

    <div
      v-show="isEditing"
      :class="$style.editOverlay"
      @click="handleToggleEditing"
    ></div>
  </div>
</template>

<style module lang="scss">
.addressContainer {
  .zipcodeField {
    @apply relative;

    .addressDetails {
      @apply flex justify-between items-center gap-0.5 sm:gap-2 relative bg-auth-bg-inputs rounded-md w-full h-[46px] sm:h-[50px];

      .addressValues {
        @apply flex flex-col gap-0 sm:gap-px px-3 text-2xs relative z-[1] whitespace-nowrap;

        .addressStreet {
          @apply text-auth-texts/60;
        }

        .addressRow {
          @apply text-auth-texts font-semibold flex items-center;

          b {
            @apply text-primary font-semibold;
          }

          .pipe {
            @apply text-auth-texts/30 mx-2;
          }

          .hasError {
            @apply text-error text-2xs inline-flex items-center gap-1;
          }
        }
      }
    }

    .buttons {
      @apply absolute right-0 top-0 flex items-center gap-1 h-[46px] sm:h-[50px] px-3 z-[2] bg-auth-bg-inputs;

      button {
        @apply text-[.64rem] px-2 h-6 flex justify-center items-center rounded  text-nowrap flex-nowrap whitespace-nowrap;

        &.changeButton {
          @apply text-auth-text-inputs bg-auth-text-inputs/20;
        }

        &.validateButton {
          @apply text-auth-text-inputs/70 text-nowrap font-medium border border-solid border-auth-text-inputs/70;
        }

        &.removeButton {
          @apply bg-error/80 text-white;
        }

        &:disabled {
          @apply opacity-40 cursor-not-allowed;
        }
      }
    }
  }

  .editFullAdress {
    @apply absolute top-full right-0 mt-2 z-30 bg-auth-bg-primary p-3 rounded-md gap-2 flex flex-wrap;
  }

  .editButton {
    @apply bg-auth-button-bg text-auth-button-text rounded-md px-3 text-lg;
  }
}

.editOverlay {
  @apply fixed top-0 left-0 w-full h-full bg-black/70 z-20 cursor-pointer;
}
</style>
